import { useEffect, useMemo, useRef } from 'react';
import { JSONContent } from '@tiptap/core';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import {
  DragId,
  INTERACTIVE_DRAG_DROP,
  InteractiveDragItem,
  KeyboardInteraction,
} from './interactive-drag-drop.constants';
import { InquisitiveActivitiesResponseVariant } from 'global/constants';
import {
  isTextImageVariant,
  isTextVariant,
} from '../../interactive-response/shared/interactive-response-shared.utils';
import { getRichtextTextValue } from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import { INTERACTIVE_RICHTEXT_EXTENSIONS } from '../../interactive-response/interactive-response-shared.constants';
import { translateElement } from './interactive-drag-drop.utils';
import { DropdownOption } from 'approot/shared/dropdown/dropdown.constants';

export const useInteractiveDrag = ({
  id,
  isDisabled,
  imageDescription,
  variant,
  richtext,
  text,
  scale,
  keyboard,
  audioUrl,
  onExpandImage,
  isAudioPlaying = false,
}: {
  id: DragId;
  isDisabled?: boolean;
  imageDescription: string;
  variant: InquisitiveActivitiesResponseVariant;
  richtext: JSONContent | null;
  text: string | null;
  scale?: number;
  keyboard: {
    target?: DragId;
    onKeyboardInteraction: (payload: KeyboardInteraction) => void;
  };
  audioUrl?: string | null;
  onExpandImage?: () => void;
  isAudioPlaying?: boolean;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const description = useMemo(() => {
    if (isTextVariant(variant)) {
      return getRichtextTextValue(richtext, INTERACTIVE_RICHTEXT_EXTENSIONS);
    }
    if (isTextImageVariant(variant)) {
      return text;
    }
    return imageDescription;
  }, [variant, richtext, text, imageDescription]);

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: INTERACTIVE_DRAG_DROP,
    item: () => ({ id, el: ref.current }),
    end: (item: InteractiveDragItem, monitor) => {
      const didDrop = monitor.didDrop();

      if (!didDrop) {
        // handle smooth transition back if not dropped on a drop area
        const initialOffset = monitor.getInitialSourceClientOffset();
        const currentOffset = monitor.getSourceClientOffset();
        if (currentOffset && initialOffset) {
          translateElement({
            element: ref.current as HTMLElement,
            currentOffset,
            initialOffset,
            scale: scale || 1,
            onComplete: () => {},
          });
        }
      }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
      currentOffset: monitor.getSourceClientOffset(),
    }),
    canDrag: () => isDisabled !== true,
  }));

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });

    if (keyboard.target === id && ref.current) {
      (ref.current.childNodes[0] as HTMLDivElement)?.focus();
    }
    //eslint-disable-next-line
  }, []);

  const kebabOptions: DropdownOption[] = useMemo(
    () => [
      ...(audioUrl
        ? [
            {
              label: `${isAudioPlaying ? 'Pause' : 'Play'} audio`,
              value: 'audio',
            },
          ]
        : []),
      ...(onExpandImage ? [{ label: 'Expand', value: 'expand' }] : []),
    ],
    [audioUrl, onExpandImage, isAudioPlaying]
  );

  return {
    description,
    isDragging,
    drag,
    preview,
    ref,
    kebabOptions,
  };
};
